import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import getApi from '@/lib/axios';
import { ExtractFnReturnType } from '@/lib/react-query';
import { CardModel, PaginatedResponse, CardFilters } from '@/features/crm/types';
import { TypesCRM } from '@/types/crm.ts';

// API antiga - retorna todos os cards
async function listCardsByStep(type: TypesCRM): Promise<CardModel[]> {
  const api = await getApi();
  const { data } = await api.get('/crm/cartoes/', {
    params: {
      tipo: type,
    },
  });
  return data;
}

// Nova API - cards de uma coluna específica com paginação e filtros
async function getStepColumnCards(
  columnId: number,
  page: number = 1,
  pageSize: number = 20,
  filters?: CardFilters
): Promise<PaginatedResponse<CardModel>> {
  const api = await getApi();
  const { data } = await api.get(`/crm/colunas/${columnId}/`, {
    params: {
      page,
      page_size: pageSize,
      ...filters
    }
  });
  
  // A API retorna { id, nome, cards: { count, results: [...] } }
  // Precisamos extrair só a parte cards
  return data.cards;
}

type QueryFnType = typeof listCardsByStep;

// Hook antigo - mantido para compatibilidade
function useListCardsByStep(type: TypesCRM) {
  return useQuery<ExtractFnReturnType<QueryFnType>>({
    queryKey: ['fetch-cards', type],
    queryFn: () => listCardsByStep(type),
    enabled: !!type,
  });
}

// Novo hook com scroll infinito e filtros
function useStepColumnCards(
  columnId: number,
  filters?: CardFilters,
  pageSize: number = 20
) {
  return useInfiniteQuery({
    queryKey: ['step-column-cards', columnId, filters, pageSize],
    queryFn: ({ pageParam = 1 }) => 
      getStepColumnCards(columnId, pageParam, pageSize, filters),
    getNextPageParam: (lastPage) => {
      // Debug: ver o que vem na resposta      
      if (lastPage.next) {
        try {
          const url = new URL(lastPage.next);
          const nextPage = Number(url.searchParams.get('page'));
          return nextPage;
        } catch (e) {
          console.error('Error parsing next URL:', e);
        }
      }
      
      // Fallback: calcular baseado na página atual
      const currentPage = lastPage.page || 1;
      const totalPages = lastPage.total_pages || 0;
      
      if (currentPage < totalPages) {
        const nextPage = currentPage + 1;
        return nextPage;
      }
      
      return undefined;
    },
    enabled: !!columnId,
    initialPageParam: 1,
  });
}

export { 
  listCardsByStep, 
  useListCardsByStep,
  getStepColumnCards,
  useStepColumnCards
};
